import { Capacitor } from "@capacitor/core";
import { readonly, type Ref, ref, watch } from "vue";

/**
 * Shared soft-keyboard state for native (Capacitor) builds. Listeners are
 * registered once at module load; refs are shared across all consumers and
 * are 0 when the keyboard is closed or on web.
 *
 * - `keyboardHeight` updates on will-show/hide (start of the animation) —
 *   use for snappy chrome changes that should lead the animation, e.g.
 *   hiding the bottom nav.
 * - `settledKeyboardHeight` updates on did-show/hide (end of the
 *   animation) — use for actions that need the new viewport in place,
 *   e.g. scrolling content into view.
 *
 * iOS overrides the static `KeyboardResize.Native` config to `None` at
 * runtime: Native shrinks the WKWebView frame invisibly to JS, so layout
 * built on `100dvh` can't reflow. With `None` we drive layout via the
 * `--visible-viewport-height` CSS variable below, which
 * `.full-height-dynamic` consumes. Android stays on Native.
 */
const keyboardHeight = ref(0);
const settledKeyboardHeight = ref(0);

if (Capacitor.isNativePlatform()) {
    const isIos = Capacitor.getPlatform() === "ios";
    import("@capacitor/keyboard")
        .then(({ Keyboard, KeyboardResize }) => {
            const noop = () => {};
            if (isIos) {
                Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(noop);
                // Hide the iOS input accessory bar (the Prev/Next/Done toolbar
                // that WKWebView injects above the keyboard on form fields).
                Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(noop);
            }
            Keyboard.addListener("keyboardWillShow", (info) => {
                keyboardHeight.value = info.keyboardHeight;
            }).catch(noop);
            Keyboard.addListener("keyboardWillHide", () => {
                keyboardHeight.value = 0;
            }).catch(noop);
            Keyboard.addListener("keyboardDidShow", (info) => {
                settledKeyboardHeight.value = info.keyboardHeight;
            }).catch(noop);
            Keyboard.addListener("keyboardDidHide", () => {
                settledKeyboardHeight.value = 0;
            }).catch(noop);
        })
        .catch(() => {
            // Plugin unavailable — non-critical, keep refs at 0.
        });

    // iOS only: mirror keyboardHeight to a CSS var so layout can shrink.
    // Android's native resize already shrinks 100dvh; setting it there
    // would double-subtract.
    if (isIos) {
        watch(keyboardHeight, (height) => {
            if (height > 0) {
                document.body.style.setProperty("--visible-viewport-height", `${window.innerHeight - height}px`);
            } else {
                document.body.style.removeProperty("--visible-viewport-height");
            }
        });
    }
}

export function useKeyboard(): {
    keyboardHeight: Readonly<Ref<number>>;
    settledKeyboardHeight: Readonly<Ref<number>>;
} {
    return {
        keyboardHeight: readonly(keyboardHeight),
        settledKeyboardHeight: readonly(settledKeyboardHeight),
    };
}
